import RoutinesDiagram from 'components/AgentTemplateEditView/EditBoards/CDHEditBoard/RoutinesSegment/RoutinesWidget';
import { wGroupIndicesATCDH } from 'components/AgentTemplateEditView/menu/cdh';
import { IErrorResponse, IGenericObject } from 'components/general/types';
import { ILogicalConfiguration, IStateTransition, TRoutine } from 'components/general/types/cdh';
import WizardSegment from 'components/general/wizards/WizardSegment';
import { useActiveEntities, useSnackbar } from 'hooks';
import { multiBlockCrud } from 'middleware/SatelliteApi/api';
import { TCompiledModel } from 'middleware/SatelliteApi/template';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TState } from './RoutinesWidget/utils';
import guidance from './guidance';

// Replaces relationships with id strings to avoid circular references and simplify state immutability
const sanitize = (block: IGenericObject) => {
  const rels = new Set(block.rels);
  const out: IGenericObject = {};
  for (const key in block) {
    if (key === 'scheduleData') {
      out[key] = block[key].reduce((acc: IGenericObject, val: IGenericObject) => {
        acc[val.id] = val.dataSide;
        return acc;
      }, {});
    } else if (key !== 'rels')
      out[key] = rels.has(key)
        ? Array.isArray(block[key])
          ? block[key].map((v: IGenericObject) => v.id)
          : block[key]?.id
        : block[key];
  }
  return out;
};

// Create routine state from model
const getRoutineState = (model: TCompiledModel) => {
  const state: TState = { deletedBlocks: [], blocks: {}, dirtyBlocks: [] };
  model.Routine.all().forEach(
    (routine) => (state.blocks[routine.id] = sanitize(routine) as TRoutine)
  );
  model.LogicalConfiguration.all().forEach(
    (config) => (state.blocks[config.id] = sanitize(config) as ILogicalConfiguration)
  );
  model.StateTransition.all().forEach(
    (transition) => (state.blocks[transition.id] = sanitize(transition) as IStateTransition)
  );
  return state;
};

const RoutinesSegment = () => {
  const {
    branch: { id: branchId },
    model,
  } = useActiveEntities();
  const [state, setState] = useState<TState>(getRoutineState(model));
  const [loading, setLoading] = useState(false);
  const [resetDiagram, setResetDiagram] = useState<(state: TState) => void>(); // Callback provided by RoutinesDiagram to reset the ReactFlow state
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // Update state when model changes
  useEffect(() => {
    setState(getRoutineState(model));
    resetDiagram && resetDiagram(getRoutineState(model));
  }, [model]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = useCallback(
    (e) => {
      setLoading(true);
      const blocks = state.dirtyBlocks.map((id) => {
        const { subroutines, ...blockData } = state.blocks[id] as TRoutine;
        return blockData;
      });
      dispatch(
        multiBlockCrud({
          branchId,
          blocks,
          delete: state.deletedBlocks,
          successCallback: () => {
            enqueueSnackbar('Routines updated successfully', {
              variant: 'success',
            });
            setLoading(false);
          },
          failureCallback: (response: IErrorResponse) => {
            const errorMessage =
              response?.error?.message ||
              'Something went wrong. Please try again. If this problem persists, please contact our support team.';
            enqueueSnackbar(
              errorMessage.length > 1000 ? errorMessage.slice(0, 1000) + '...' : errorMessage
            );
            setLoading(false);
          },
        })
      );
      e.preventDefault(); // Prevents page reload
    },
    [state, dispatch, branchId, enqueueSnackbar, setLoading]
  );

  const onReset = useCallback(() => {
    const newState = getRoutineState(model);
    setState(newState);
    resetDiagram && resetDiagram(newState);
  }, [model, resetDiagram, setState]);

  return (
    <WizardSegment
      title="Routines"
      loading={loading}
      disableSubmit={!state.dirtyBlocks.length && !state.deletedBlocks.length}
      guidance={guidance}
      index={wGroupIndicesATCDH.ROUTINES}
      xray={state.blocks}
      fullscreen
      onSubmit={onSubmit}
      onReset={onReset}
    >
      <RoutinesDiagram state={state} setState={setState} setResetDiagram={setResetDiagram} />
    </WizardSegment>
  );
};

export default RoutinesSegment;
